/**
 * Tool handler — server-side LLM tool call events.
 *
 * Handles: llm.tool_call (non-chat only — chat tool calls handled by chat.ts)
 *
 * Business logic:
 *   - §7.6 Filter re-emissions (server may re-send; only emit once per msgId)
 *   - Camelize tool_calls → toolCalls
 *   - Auto-execute Tool objects registered on the agent
 *   - Emit on call + agent (via proxy)
 */

import type { EventHandler, DispatchContext } from "../handler.js";
import type { WireEvent } from "../../protocol/wire.js";
import type { ToolCallEvent, ToolCallItem } from "../../protocol/events.js";
import type { Agent } from "../../domain/agent.js";
import { noopLogger, type Logger } from "../../kernel/logger.js";

export class ToolHandler implements EventHandler {
    readonly events = ["llm.tool_call"] as const;

    /** Track emitted msg_ids to prevent duplicate emissions. */
    #emittedMsgIds = new Set<string>();

    handle(wire: WireEvent, ctx: DispatchContext): boolean {
        // Chat tool calls are handled by the ChatHandler
        if (wire.call_id && (wire.call_id as string).startsWith("chat-")) {
            return false; // Let ChatHandler handle it
        }

        const callId = wire.call_id as string;
        if (!callId) return false;

        // Resolve agent — try wire.agent_id first, then search all agents for the call
        let agent: Agent | null = wire.agent_id
            ? ctx.agent(wire.agent_id)
            : null;

        if (!agent) {
            // Server didn't include agent_id (WhatsApp, legacy voice) —
            // search all agents for one that owns this call or has tools
            agent = this.#findAgentByCall(callId, ctx);
        }
        if (!agent) return false;

        const call = agent._getCall(callId);

        const msgId = (wire.msg_id ?? wire.message_id ?? "") as string;

        // Deduplicate — server may re-send tool calls
        if (msgId && this.#emittedMsgIds.has(msgId)) {
            return true;
        }
        if (msgId) this.#emittedMsgIds.add(msgId);

        // Transform wire tool_calls → SDK toolCalls
        const rawToolCalls = (wire.tool_calls ?? []) as Array<Record<string, unknown>>;
        const toolCalls: ToolCallItem[] = rawToolCalls.map(tc => ({
            id: (tc.id ?? "") as string,
            name: (tc.name ?? (tc.function as any)?.name ?? "") as string,
            arguments: (tc.arguments ?? (tc.function as any)?.arguments ?? "{}") as string,
        }));

        const event: ToolCallEvent = {
            event: "llm.toolCall",
            callId,
            toolCalls,
            msgId,
        };

        // Emit event on call (so agent proxy picks it up too)
        if (call) {
            call._emitWire("llm.toolCall", event);
            // Push tool_calls to incremental history — unless every called tool
            // is ephemeral, in which case the whole round is left out of history.
            const toolList = agent._getTools();
            const tMap = new Map(toolList.map(t => [t.name, t]));
            const allEphemeral = toolCalls.length > 0 &&
                toolCalls.every(tc => tMap.get(tc.name)?.ephemeral ?? false);
            if (!allEphemeral) {
                call._pushMessage({
                    role: "assistant",
                    tool_calls: toolCalls.map(tc => ({
                        id: tc.id,
                        type: "function",
                        function: { name: tc.name, arguments: tc.arguments },
                    })),
                });
            }
        } else {
            // No Call object (WhatsApp sessions) — emit directly on agent
            agent._emitWire("llm.toolCall", event, null as any);
        }

        // Auto-execute registered Tool objects
        const tools = agent._getTools();
        if (tools.length > 0) {
            if (call) {
                void autoExecuteTools(tools, event, call, ctx.logger);
            } else {
                // WhatsApp: build a lightweight proxy with toolResult
                void autoExecuteTools(tools, event, {
                    toolResult: (mId: string, results: Array<{ toolCallId: string; result: unknown; ephemeral?: boolean }>) => {
                        ctx.send({
                            event: "llm.tool_result",
                            call_id: callId,
                            msg_id: mId,
                            results: results.map(r => ({
                                tool_call_id: r.toolCallId,
                                result: r.result,
                                ...(r.ephemeral ? { ephemeral: true } : {}),
                            })),
                        });
                    },
                } as any, ctx.logger);
            }
        }

        return true;
    }

    /** Find the agent that owns the given call, or the first agent with tools. */
    #findAgentByCall(callId: string, ctx: DispatchContext): Agent | null {
        const agents = ctx.allAgents();

        // First pass: find agent that owns the call
        for (const a of agents) {
            if (a._getCall(callId)) return a;
        }

        // Second pass: find agent with tools (for WhatsApp where no Call object exists)
        for (const a of agents) {
            if (a._getTools().length > 0) return a;
        }

        return null;
    }

}

/**
 * Auto-execute registered Tool objects for a tool-call event and send the
 * results back via `call.toolResult`. Shared by the voice/WhatsApp ToolHandler
 * and the ChatHandler (chat tool calls auto-execute the same way).
 *
 * `log` is where the per-call trace goes. This is LIBRARY code running inside
 * every consumer's process, so it must never write to console on its own — the
 * caller passes ctx.logger and decides whether debug lines are visible.
 */
export async function autoExecuteTools(
    tools: Array<{ name: string; schema: { parse: (input: unknown) => any }; execute: (args: any, call: any) => unknown | Promise<unknown>; ephemeral?: boolean; noFollowup?: boolean }>,
    event: ToolCallEvent,
    call: { toolResult: (msgId: string, results: Array<{ toolCallId: string; result: unknown; ephemeral?: boolean; noFollowup?: boolean }>) => void },
    log: Logger = noopLogger,
): Promise<void> {
    const toolMap = new Map(tools.map(t => [t.name, t]));
    const names = event.toolCalls.map(tc => tc.name);
    log.debug(`tool_call [${names.join(", ")}] msgId=${event.msgId.slice(0, 12)}`);

    const results = await Promise.all(
        event.toolCalls.map(async (tc) => {
            const t = toolMap.get(tc.name);
            if (!t) {
                log.debug(`  ${tc.name} → unknown tool`);
                return { toolCallId: tc.id, result: { error: `Unknown tool: ${tc.name}` }, ephemeral: false, noFollowup: false };
            }

            const ephemeral = t.ephemeral ?? false;
            const noFollowup = t.noFollowup ?? false;
            try {
                const args = t.schema.parse(JSON.parse(tc.arguments));
                log.debug(`  ${tc.name}(${JSON.stringify(args).slice(0, 120)})`);
                const result = await t.execute(args, call as any);
                const preview = JSON.stringify(result).slice(0, 200);
                log.debug(`  ${tc.name} → ${preview}${ephemeral ? " (ephemeral)" : ""}${noFollowup ? " (noFollowup)" : ""}`);
                return { toolCallId: tc.id, result, ephemeral, noFollowup };
            } catch (err: any) {
                log.debug(`  ${tc.name} → error: ${err.message ?? err}`);
                return { toolCallId: tc.id, result: { error: err.message ?? String(err) }, ephemeral, noFollowup };
            }
        }),
    );

    call.toolResult(event.msgId, results);

    // Push tool results to incremental history — skip ephemeral ones, they are
    // never persisted (the server drops them from the LLM context too).
    if ("_pushMessage" in call) {
        for (const r of results) {
            if (r.ephemeral) continue;
            (call as any)._pushMessage({
                role: "tool",
                tool_call_id: r.toolCallId,
                content: typeof r.result === "string" ? r.result : JSON.stringify(r.result),
            });
        }
    }
}

